import { Checkbox, RangeSlider, Select } from '@shopify/polaris';
import { useShallow } from 'zustand/react/shallow';
import { useWidgetStore } from '../../../store/widget.store';
import { CustomColorPicker } from '../../color-picker';
import { TabSection } from '../../tab-section';
import type { WidgetType } from '../../../widgets/widgets';

const animationOptions = [
    { label: 'None', value: 'none' },
    { label: 'Shimmer sweep', value: 'shimmer' },
    { label: 'Glow pulse', value: 'glow' },
    { label: 'Pulse border', value: 'pulse-border' },
    { label: 'Gradient shift', value: 'gradient-shift' },
];

const gradientDirectionOptions = [
    { label: 'Left → Right', value: 'to right' },
    { label: 'Right → Left', value: 'to left' },
    { label: 'Top → Bottom', value: 'to bottom' },
    { label: 'Bottom → Top', value: 'to top' },
    { label: 'Diagonal ↘', value: '135deg' },
    { label: 'Diagonal ↗', value: '45deg' },
];

const shadowOptions = [
    { label: 'Light', value: 'light' },
    { label: 'Medium', value: 'medium' },
    { label: 'Heavy', value: 'heavy' },
];

type Props = {
    widgetType: WidgetType;
};
export function DesignTab({ widgetType }: Readonly<Props>) {
    const {
        verticalPadding,
        horizontalPadding,
        borderColor,
        borderRadius,
        borderWidth,
        backgroundColor,
        setVerticalPadding,
        setHorizontalPadding,
        setBorderRadius,
        setBorderWidth,
        setBorderColor,
        setBackgroundColor,
        fullWidth,
        setFullWidth,
        topMargin,
        bottomMargin,
        setTopMargin,
        setBottomMargin,
        gradientEnabled,
        setGradientEnabled,
        gradientColor,
        setGradientColor,
        gradientDirection,
        setGradientDirection,
        shadowEnabled,
        setShadowEnabled,
        shadowIntensity,
        setShadowIntensity,
        animationStyle,
        setAnimationStyle,
    } = useWidgetStore(
        useShallow(state => ({
            verticalPadding: state.verticalPadding,
            horizontalPadding: state.horizontalPadding,
            borderRadius: state.borderRadius,
            borderWidth: state.borderWidth,
            borderColor: state.borderColor,
            backgroundColor: state.backgroundColor,
            setVerticalPadding: state.setVerticalPadding,
            setHorizontalPadding: state.setHorizontalPadding,
            setBorderRadius: state.setBorderRadius,
            setBorderWidth: state.setBorderWidth,
            setBorderColor: state.setBorderColor,
            setBackgroundColor: state.setBackgroundColor,
            fullWidth: state.fullWidth,
            setFullWidth: state.setFullWidth,
            topMargin: state.topMargin,
            bottomMargin: state.bottomMargin,
            setTopMargin: state.setTopMargin,
            setBottomMargin: state.setBottomMargin,
            gradientEnabled: state.gradientEnabled,
            setGradientEnabled: state.setGradientEnabled,
            gradientColor: state.gradientColor,
            setGradientColor: state.setGradientColor,
            gradientDirection: state.gradientDirection,
            setGradientDirection: state.setGradientDirection,
            shadowEnabled: state.shadowEnabled,
            setShadowEnabled: state.setShadowEnabled,
            shadowIntensity: state.shadowIntensity,
            setShadowIntensity: state.setShadowIntensity,
            animationStyle: state.animationStyle,
            setAnimationStyle: state.setAnimationStyle,
        }))
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <TabSection header="Container">
                <div>
                    <RangeSlider
                        output
                        label="Vertical padding"
                        value={verticalPadding}
                        suffix={`${verticalPadding}px`}
                        min={0}
                        max={100}
                        step={1}
                        onChange={setVerticalPadding}
                    />
                </div>
                <div>
                    <RangeSlider
                        output
                        label="Horizontal padding"
                        value={horizontalPadding}
                        suffix={`${horizontalPadding}px`}
                        min={0}
                        max={500}
                        step={1}
                        onChange={setHorizontalPadding}
                    />
                </div>
                <div>
                    <Checkbox label="Full width" checked={fullWidth} onChange={setFullWidth} />
                </div>
            </TabSection>
            <TabSection header="Colors">
                <div>
                    <CustomColorPicker
                        onChange={v => setBackgroundColor(v)}
                        value={backgroundColor}
                        title="Background color"
                    />
                </div>
                <div>
                    <Checkbox label="Enable gradient" checked={gradientEnabled} onChange={setGradientEnabled} />
                </div>
                {gradientEnabled && (
                    <>
                        <div>
                            <CustomColorPicker
                                onChange={v => setGradientColor(v)}
                                value={gradientColor}
                                title="Gradient end color"
                            />
                        </div>
                        <div>
                            <Select
                                label="Gradient direction"
                                options={gradientDirectionOptions}
                                value={gradientDirection}
                                onChange={setGradientDirection}
                            />
                        </div>
                    </>
                )}
            </TabSection>
            <TabSection header="Effects">
                <div>
                    <Select
                        label="Animation"
                        options={animationOptions}
                        value={animationStyle}
                        onChange={setAnimationStyle}
                    />
                </div>
                <div>
                    <Checkbox label="Drop shadow" checked={shadowEnabled} onChange={setShadowEnabled} />
                </div>
                {shadowEnabled && (
                    <div>
                        <Select
                            label="Shadow intensity"
                            options={shadowOptions}
                            value={shadowIntensity}
                            onChange={setShadowIntensity}
                        />
                    </div>
                )}
            </TabSection>
            <TabSection header="Margins">
                <div>
                    <RangeSlider
                        output
                        label="Top margin"
                        value={topMargin}
                        suffix={`${topMargin}px`}
                        min={0}
                        max={500}
                        step={1}
                        onChange={setTopMargin}
                    />
                </div>
                <div>
                    <RangeSlider
                        output
                        label="Bottom margin"
                        value={bottomMargin}
                        suffix={`${bottomMargin}px`}
                        min={0}
                        max={500}
                        step={1}
                        onChange={setBottomMargin}
                    />
                </div>
            </TabSection>
            <TabSection header="Border">
                <div>
                    <RangeSlider
                        output
                        label="Border width"
                        value={borderWidth}
                        suffix={`${borderWidth}px`}
                        min={0}
                        max={20}
                        step={1}
                        onChange={setBorderWidth}
                    />
                </div>
                <div>
                    <RangeSlider
                        output
                        label="Border radius"
                        value={borderRadius}
                        suffix={`${borderRadius}px`}
                        min={0}
                        max={100}
                        step={1}
                        onChange={setBorderRadius}
                    />
                </div>
                <div>
                    <CustomColorPicker onChange={v => setBorderColor(v)} value={borderColor} title="Border color" />
                </div>
            </TabSection>
        </div>
    );
}
